import { useEffect, useId, useSyncExternalStore } from "react";

/**
 * App-wide record of forms with unsaved edits. Module-level rather than
 * context so it works for forms rendered outside AppContextProvider
 * (widgets, onboarding) and for AppVersionProvider, which sits above it.
 */
const dirtyFormIds = new Set<string>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach(listener => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getHasDirtyForms = () => dirtyFormIds.size > 0;

export const useRegisterDirtyForm = (isDirty: boolean) => {
  const id = useId();

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    dirtyFormIds.add(id);
    notify();
    return () => {
      dirtyFormIds.delete(id);
      notify();
    };
  }, [id, isDirty]);
};

export const useHasDirtyForms = () =>
  useSyncExternalStore(subscribe, getHasDirtyForms);
